import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { NbDialogRef } from "@nebular/theme";
import { ModalConfirmationService } from "../confirmation-modal/confirmation-modal.service";
import { NgForm } from "@angular/forms";
import { SalesOrderService } from "../../sales-order.service";
import { CreateSalesOrderModalComponent } from "../create-sales-order/create-sales-order.component";
import { StatusCondition } from "../../constants/condition.constant";
import { TapeHelpService } from "../../tape-helper.service";
import { getCurrencySymbol } from "@angular/common";
import { ConfigurationService } from "../../configuration.service";
import { NewSalesOrderBatch } from "../../../insurgo-interfaces/sales-order-batch";
import { ToastService } from "../../toast.service";

@Component({
  templateUrl: "./create-sales-order-batch.html",
  styles: [
    `
      ::ng-deep .ng-dropdown-panel.inModal {
        z-index: 1060 !important;
      }
    `,
  ],
})
export class CreateSalesOrderBatchModalComponent implements OnInit {
  @Input() salesOrderBatchUUID;
  @Input() salesOrderUUID;
  @Input() hasTapes: boolean;

  originalData: Object;
  loaded = false;
  salesOrderBatch: NewSalesOrderBatch = {
    type: null,
    manufacturer: null,
    quantity: 0,
    pricePerTape: 0,
    statusCondition: null,
  };

  formError: any = {
    submitting: false,
    error: false,
    success: false,
  };

  public currencySymbol: string = getCurrencySymbol(
    this.configurationService.getConfigurationSettings().defaultCurrency,
    "narrow"
  );

  public conditionOptions = [
    { label: "Used", value: StatusCondition.USED },
    { label: "New sealed", value: StatusCondition.NEW_SEALED },
    { label: "Pre-labelled", value: StatusCondition.PRE_LABELLED },
  ];

  public tapeManufacturerOptions: any[] = [];
  public tapeTypeOptions: any[] = [];

  @ViewChild("createSalesOrder") createSalesOrderForm: NgForm;

  constructor(
    protected ref: NbDialogRef<CreateSalesOrderModalComponent>,
    private _modalConfirmationService: ModalConfirmationService,
    private _salesOrderService: SalesOrderService,
    private toastService: ToastService,
    private readonly _tapeHelpService: TapeHelpService,
    private configurationService: ConfigurationService
  ) {}
  private async getTapeManufacturers() {
    this.tapeManufacturerOptions = [];
    const manufacturers = await this._tapeHelpService
      .getAllManufacturers()
      .toPromise();
    this.tapeManufacturerOptions = manufacturers.filter(
      (manufacturer) =>
        manufacturer.manufacturerName &&
        manufacturer.manufacturerName.trim() !== ""
    );
  }

  private async getTapeTypes() {
    this.tapeTypeOptions = await this._tapeHelpService
      .getAllModels()
      .toPromise();
  }

  async ngOnInit() {
    this.originalData = this.deepCopy(<Object>this.salesOrderBatch);
    if (this.salesOrderBatchUUID) {
      await this.getData();
    } else {
      this.loaded = true;
    }
    this.getTapeManufacturers();
    this.getTapeTypes();
  }
  async getData() {
    try {
      this.loaded = false;
      let data = <any>(
        await this._salesOrderService
          .getSalesOrderBatch(this.salesOrderBatchUUID)
          .toPromise()
      );
      this.salesOrderBatch = this.deepCopy(data);
      this.originalData = this.deepCopy(data);

      this.loaded = true;
    } catch (error) {
      this.toastService.showToast(
        "error",
        "Fetch Error",
        `Error occurred getting the sales order data!`
      );
      this.ref.close(false);
    }
  }

  submit(): void {
    this.formError.submitting = true;
    this.formError.success = false;
    this.formError.error = false;
    if (this.salesOrderBatch.quantity <= 0) {
      this.formError.error = true;
      this.toastService.showToast(
        "warning",
        "Invalid Quantity",
        `Quantity must be greater than 0`
      );
      return;
    }
    if (this.salesOrderBatch.pricePerTape < 0) {
      this.formError.error = true;
      this.toastService.showToast(
        "warning",
        "Invalid Price",
        `Invalid price`
      );
      return;
    }
    if (this.salesOrderBatchUUID) {
      this._salesOrderService
        .updateSalesOrderBatch(this.salesOrderBatchUUID, this.salesOrderBatch)
        .subscribe(
          (response) => {
            this.formError.success = true;
            setTimeout(
              function () {
                this.toastService.showToast(
                  "success",
                  "Sales Order Batch Edited",
                  `Sales Order Batch was edited successfully!`
                );
                this.ref.close(true);
              }.bind(this),
              3000
            );
          },
          (err) => {
            this.toastService.showToast(
              "error",
              "Error",
              err
            );
            this.formError.error = true;
            setTimeout(
              function () {
                this.formError.submitting = false;
              }.bind(this),
              3000
            );
          }
        );
    } else {
      this._salesOrderService
        .createSalesOrderBatch(this.salesOrderUUID, this.salesOrderBatch)
        .subscribe(
          (response) => {
            this.formError.success = true;
            setTimeout(
              function () {
                this.toastService.showToast(
                  "success",
                  "Sales Order Batch Created",
                  `A new sales order batch was created successfully!`
                );
                this.ref.close(true);
              }.bind(this),
              3000
            );
          },
          (err) => {
            this.toastService.showToast(
              "error",
              "Error",
              err
            );
            this.formError.error = true;
            setTimeout(
              function () {
                this.formError.submitting = false;
              }.bind(this),
              3000
            );
          }
        );
    }
  }

  async resetForm() {
    await this.getData();
    this._markFormPristine();
  }

  private _markFormPristine(): void {
    const {
      createSalesOrderForm: { value: formValueSnap },
    } = this; // const formValueSnap = this.myForm.value;
    this.createSalesOrderForm.reset(formValueSnap);
    Object.keys(this.createSalesOrderForm.controls).forEach((control) => {
      this.createSalesOrderForm.controls[control].markAsPristine();
      this.createSalesOrderForm.controls[control].markAsUntouched();
    });
  }

  reset(form: NgForm) {
    this.salesOrderBatch = this.deepCopy(<Object>this.originalData);
    this.formError.submitting = false;
    this.formError.success = false;
    setTimeout(
      function () {
        this._markFormPristine();
      }.bind(this),
      500
    );
  }

  async cancel(dirty) {
    if (dirty) {
      const result = await this._modalConfirmationService.confirm(
        "Still Editing the form?",
        "You have not finished editing, are you sure you want to cancel?"
      );
      if (result === "confirm") {
        this.ref.close(false);
      } else if (result === "cancel") {
      } else if (result === "decline") {
      }
    } else {
      this.ref.close(false);
    }
  }

  displayFieldCss(form: NgForm, field: any) {
    if (form.dirty) {
      if (field.touched && field.invalid) {
        //invalid
        return "danger";
      } else if (field.valid) {
        //valid
        return "success";
      } else {
        //needs to be filled in
        return "warning";
      }
    } else {
      return "basic";
    }
  }

  deepCopy(obj): any {
    let copy;
    // Handle the 3 simple types, and null or undefined
    if (obj == undefined || typeof obj !== "object") {
      return obj;
    }
    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());

      return copy;
    }
    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }

      return copy;
    }
    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(<Object>obj[attr]);
        }
      }

      return copy;
    }
    throw new Error(`Unable to copy obj! Its type isn't supported.`);
  }

  public formatPricePerTape() {
    const val = this.salesOrderBatch.pricePerTape;
    if (val != null && !isNaN(val)) {
      this.salesOrderBatch.pricePerTape = parseFloat(val.toFixed(2));
    }
  }
}
